import 'react-app-polyfill/ie9';
import 'react-app-polyfill/stable';

import React from 'react';
import ReactDOM from 'react-dom';

import { Router, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';

import initI18n from './services/i18n';
import { history } from './services/routing';
import { store } from './services/redux';

import { chooseVwBrand, configKey, getStyles } from './config';

import { Analytics } from './services/analytics';

// import { extractTrackingCode } from './services/common/tracking'
import { DEFAULT_DATALAYER } from './datalayer';
import { ErrorPage } from './screens/ErrorPage/ErrorPage';
import { set } from 'lodash';
import { sanitizeTransactionIdAtQueryParams } from './services/common/format';

const log = (...msgs) => {
  // eslint-disable-next-line
  if (process.env.NODE_ENV === 'development') console.log(...msgs);
};

window.log = log;

const ConnectedApp = React.lazy(() => {
  return import('./App');
});

const PositionRestore = () => {
  const location = useLocation();
  if (location.hash && location.hash !== '') {
    setTimeout(() => {
      const id = location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }, 0);
  } else {
    document.body.style.overflowAnchor = 'none';
    setTimeout(() => {
      document.body.style.overflowAnchor = 'auto';
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }, 0);
  }

  return null;
};

const getConfig = brand => {
  return import(`./config/${brand}`);
};

function Init() {
  const [loaded, setLoaded] = React.useState(false);
  // const [stylesLoaded, setStylesLoaded] = React.useState(false);
  const [isError, setIsError] = React.useState(false);
  const [config, setConfig] = React.useState();

  React.useEffect(() => {
    getConfig(configKey).then(module => setConfig(module.default));
  }, [configKey]);

  React.useEffect(() => {
    if (config && config.appConfig.resourceKey) {
      Promise.all([
        initI18n(config.appConfig.resourceKey),
        getStyles(config.appConfig.resourceKey)
      ]).then(() => {
        setLoaded(true);
      });
    }
  }, [config]);

  React.useEffect(() => {
    if (config && config.appConfig.brandName) {
      const digitalData = DEFAULT_DATALAYER;
      const searchParams = new URLSearchParams(window.location.search);
      const transactionId = sanitizeTransactionIdAtQueryParams();

      set(digitalData, Analytics.keys.stagingEnvironment(), process.env.REACT_APP_ENV);
      set(digitalData, Analytics.keys.brand(), chooseVwBrand(config.appConfig.brandName));
      set(digitalData, Analytics.keys.transactionId(), transactionId || null);
      set(digitalData, Analytics.keys.context(), searchParams.get('contextID') || null);

      Analytics.setDefaults(digitalData);
      Analytics.pageBottom();
    }
  }, [config]);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (window.location.pathname.startsWith('/vwfs')) {
      if (window.location.pathname.includes('/page')) {
        setIsError(false);
        return;
      }

      if (
        !(
          searchParams.get('contextID') ||
          searchParams.get('transactionId') ||
          searchParams.get('id')
        )
      ) {
        setIsError(true);
      }
    }
  }, [window.location]);

  if (isError) {
    return <ErrorPage />;
  }

  return loaded ? (
    <React.Suspense fallback={null}>
      <Provider store={store}>
        <Router history={history}>
          <PositionRestore />
          <React.Fragment>
            <ConnectedApp config={config} />
          </React.Fragment>
        </Router>
      </Provider>
    </React.Suspense>
  ) : null;
}

if (module.hot) {
  module.hot.accept(render);
}

function render() {
  const rootElement = document.getElementById('root');
  ReactDOM.render(<Init />, rootElement);
}

render();
